import { AxiosInstance } from 'axios';
import { MindSDK, MindSDKOptions } from 'mind-sdk-web';
import { intermindConfig, IntermindConfig } from './config';
import { HttpClientFactory } from './http/HttpClientFactory';
import { AdminResource } from './modules/admin';
import { AIResource } from './modules/ai';
import { AskResource } from './modules/ask';
import { AssistantResource } from './modules/assistant';
import { AuthResource } from './modules/auth';
import { BillingResource } from './modules/billing';
import { CalendarResource } from './modules/calendar';
import { ConferenceResource } from './modules/conference';
import { DeviceRegistry } from './modules/device/models/DeviceRegistry';
import { FeedbackResource } from './modules/feedback';
import { TelemetryResource } from './modules/telemetry';

// Export types
export * from './types';

export const X_BYPASS_ERROR_HANDLER = 'X-Bypass-Error-Handler';

export interface IntermindRequestOptions {
  headers?: Record<string, string>;
  bypassErrorHandler?: boolean;
}

class Intermind {
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private _deviceRegistry: DeviceRegistry | null = null;
  private _httpClient: AxiosInstance;

  private _authResource: AuthResource;
  private _aiResource: AIResource;
  private _askResource: AskResource;
  private _assistantResource: AssistantResource;
  private _adminResource: AdminResource;
  private _billingResource: BillingResource;
  private _conferenceResource: ConferenceResource;
  private _calendarResource: CalendarResource;
  private _feedback: FeedbackResource;
  private _telemetry: TelemetryResource;

  constructor() {
    this._httpClient = HttpClientFactory.create();
    this._authResource = new AuthResource();
    this._aiResource = new AIResource();
    this._askResource = new AskResource();
    this._assistantResource = new AssistantResource();
    this._adminResource = new AdminResource();
    this._billingResource = new BillingResource();
    this._conferenceResource = new ConferenceResource();
    this._calendarResource = new CalendarResource();
    this._feedback = new FeedbackResource();
    this._telemetry = new TelemetryResource();
  }

  // Method to set locale
  setLocale(locale: string): void {
    intermindConfig.setConfig({ locale });
  }

  get httpClient(): AxiosInstance {
    return this._httpClient;
  }

  // Method to set request options for the next call
  withOptions(options: IntermindRequestOptions): Intermind {
    const newInstance = new Intermind();
    Object.assign(newInstance, this);

    if (options.headers) {
      this._httpClient.defaults.headers.common = {
        ...this._httpClient.defaults.headers.common,
        ...options.headers,
      };
    }

    if (options.bypassErrorHandler) {
      this._httpClient.defaults.headers.common[X_BYPASS_ERROR_HANDLER] = 'true';
    }

    return newInstance;
  }

  async setup(configOptions: IntermindConfig) {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        await intermindConfig.setConfig(configOptions);

        const mindSdkOptions = new MindSDKOptions();
        mindSdkOptions.setUseVp9ForSendingVideo(true);
        await MindSDK.initialize(mindSdkOptions);

        this._deviceRegistry = new DeviceRegistry();
        this.initialized = true;
      } catch (error) {
        this.initPromise = null;
        throw error;
      }
    })();

    return this.initPromise;
  }

  private checkInitialized() {
    if (!this.initialized) {
      throw new Error('Intermind SDK is not initialized. Call setup() first.');
    }
  }

  get auth() {
    this.checkInitialized();
    return this._authResource;
  }

  get ai() {
    this.checkInitialized();
    return this._aiResource;
  }

  get ask() {
    this.checkInitialized();
    return this._askResource;
  }

  get assistant() {
    this.checkInitialized();
    return this._assistantResource;
  }

  get admin() {
    this.checkInitialized();
    return this._adminResource;
  }

  get conference() {
    this.checkInitialized();
    return this._conferenceResource;
  }

  get billing() {
    this.checkInitialized();
    return this._billingResource;
  }

  get deviceRegistry() {
    this.checkInitialized();
    return this._deviceRegistry;
  }

  get calendar() {
    this.checkInitialized();
    return this._calendarResource;
  }

  get feedback() {
    this.checkInitialized();
    return this._feedback;
  }

  get telemetry() {
    this.checkInitialized();
    return this._telemetry;
  }
}

const intermind = new Intermind();
export default intermind;
